/**
 * Pure drawing state for the Lyktan hearth-drawing surface.
 *
 * Why a separate module: the Svelte component (`HearthDrawing.svelte`)
 * focuses on the DOM/MapLibre side; this module owns the data shape and
 * the legal transitions. Keeping it pure makes the unit tests fast and
 * means we can reason about undo/redo and mode switches without spinning
 * up a map.
 *
 * Domain notes:
 * - In `snap-to-road` mode the user clicks **waypoints**; each waypoint
 *   triggers a backend route fetch that returns a polyline segment between
 *   the previous waypoint and the new one. We store both the waypoint
 *   list (so undo can drop the last click) and the resolved segments (so
 *   the map shows the snapped path while drawing).
 * - In `free` mode (S-04-05) every click adds a vertex directly. There
 *   are no waypoints separate from segments — the polyline is the click
 *   list. We model both modes uniformly via segments so callers don't
 *   have to switch on mode when rendering.
 *
 * Hard rules:
 * - No notes/description on the drawing surface (UI-kontrakt vy 7 says
 *   `description` lives only on the saved-hearth detail panel, not while
 *   drawing).
 * - No persisted state: the state lives in memory; nothing in
 *   localStorage or sessionStorage. A page reload starts a fresh draw.
 */

import type { Coordinate, DrawingMode, Polyline } from './types';
import { DEFAULT_DRAWING_MODE } from './types';

/**
 * One segment of the in-progress drawing. Always at least two points
 * (matches Polyline invariants). In snap-to-road mode each segment maps
 * to one backend route response; in free mode the whole drawing is a
 * single segment that grows one click at a time.
 */
export interface DrawingSegment {
  readonly mode: DrawingMode;
  readonly points: ReadonlyArray<Coordinate>;
}

export interface DrawingState {
  readonly mode: DrawingMode;
  readonly waypoints: ReadonlyArray<Coordinate>;
  readonly segments: ReadonlyArray<DrawingSegment>;
}

export function createInitialDrawingState(mode: DrawingMode = DEFAULT_DRAWING_MODE): DrawingState {
  return { mode, waypoints: [], segments: [] };
}

/**
 * Switch drawing mode without throwing away accepted segments.
 *
 * Story S-04-05 acceptance: "Polyline kan kombineras: del snap, del fri,
 * del snap igen". To enable that, mode-switch keeps every accepted
 * segment and the waypoint list. What it **does** clear is the
 * "in-progress free-draw stroke": if the user was actively drawing free
 * mode and toggles away mid-stroke, that stroke is committed first via
 * `commitFreeStrokeIfAny` so it survives the switch.
 *
 * Undo semantics across modes stay sane because `undoLast` is mode-aware
 * and the segment list itself is mode-tagged per entry.
 */
export function setMode(state: DrawingState, next: DrawingMode): DrawingState {
  if (state.mode === next) return state;

  // If we're leaving free-draw with an open stroke (waypoints recorded
  // but not yet captured as a segment because the user toggled before
  // releasing), make sure the stroke is committed before we switch. In
  // practice the controller calls `commitFreeStrokeIfAny` on mouseup,
  // but we double-check here so the state machine is robust to
  // out-of-order events.
  const committed = state.mode === 'free' ? commitFreeStrokeIfAny(state) : state;

  // Clear waypoints because they are mode-specific (snap-to-road tracks
  // route endpoints; free mode tracks vertices of the live stroke). The
  // accepted segments survive — that's what enables the multi-segment
  // hearth combination per story acceptance.
  return { mode: next, waypoints: [], segments: committed.segments };
}

/**
 * Capture the current free-mode stroke (if any) as a finished segment
 * and clear the running waypoint list. No-op outside free mode or when
 * fewer than two vertices have been collected.
 */
export function commitFreeStrokeIfAny(state: DrawingState): DrawingState {
  if (state.mode !== 'free') return state;
  if (state.waypoints.length < 2) {
    // Either nothing drawn or a single click: drop the dangling vertex
    // since a polyline needs at least two points.
    if (state.waypoints.length === 0) return state;
    return { mode: state.mode, waypoints: [], segments: state.segments };
  }
  // Avoid double-commit: if the latest segment already matches the
  // running waypoints (free-mode rebuilds the segment on every vertex),
  // we just clear waypoints without appending a duplicate.
  const latest = state.segments[state.segments.length - 1];
  const sameAsLatest =
    latest !== undefined &&
    latest.mode === 'free' &&
    latest.points.length === state.waypoints.length &&
    latest.points.every(
      (p, i) =>
        p.latitude === state.waypoints[i].latitude && p.longitude === state.waypoints[i].longitude,
    );

  const segments = sameAsLatest
    ? state.segments
    : [...state.segments, { mode: 'free' as const, points: [...state.waypoints] }];

  return { mode: state.mode, waypoints: [], segments };
}

/**
 * Snap-to-road click. Caller has already resolved the segment via the
 * route service. `segmentPoints` is the GraphHopper-returned polyline
 * between the previous waypoint and the new waypoint. For the first
 * waypoint there is no segment yet (we just record it).
 */
export function appendSnapWaypoint(
  state: DrawingState,
  waypoint: Coordinate,
  segmentPoints: ReadonlyArray<Coordinate> | null,
): DrawingState {
  if (state.mode !== 'snap-to-road') return state;

  const nextWaypoints = [...state.waypoints, waypoint];

  // First click: just record the waypoint. No segment exists yet because
  // a polyline needs at least two points.
  if (state.waypoints.length === 0) {
    return { mode: state.mode, waypoints: nextWaypoints, segments: state.segments };
  }

  // Defensive: caller must supply >=2-point segment for non-first clicks.
  // If it's missing or too short, we drop the click (it would otherwise
  // violate Polyline invariants downstream). Component surfaces the
  // user-facing error before reaching us.
  if (!segmentPoints || segmentPoints.length < 2) {
    return state;
  }

  const segment: DrawingSegment = {
    mode: 'snap-to-road',
    points: [...segmentPoints],
  };
  return {
    mode: state.mode,
    waypoints: nextWaypoints,
    segments: [...state.segments, segment],
  };
}

/**
 * Free-draw click. Adds the click to the current free-mode stroke,
 * rebuilding the running segment from `waypoints` so undo can drop the
 * last vertex without a separate stack.
 *
 * S-04-05 multi-segment behaviour: prior segments (snap or free, from
 * earlier mode toggles) are preserved. The "current" stroke is always
 * the segment that matches the running waypoint list — we rebuild that
 * one entry on each click rather than appending a fresh segment.
 */
export function appendFreeVertex(state: DrawingState, point: Coordinate): DrawingState {
  if (state.mode !== 'free') return state;

  const nextWaypoints = [...state.waypoints, point];

  // `priorSegments` are anything finished before the current stroke
  // started. We detect "current stroke" by checking the trailing
  // segment: if it's a free-mode segment whose points are the
  // *previous* waypoint list, it belongs to this in-progress stroke and
  // we replace it. Otherwise the new stroke begins fresh.
  let priorSegments = state.segments;
  const trailing = state.segments[state.segments.length - 1];
  if (
    trailing !== undefined &&
    trailing.mode === 'free' &&
    state.waypoints.length >= 2 &&
    trailing.points.length === state.waypoints.length &&
    trailing.points.every(
      (p, i) =>
        p.latitude === state.waypoints[i].latitude && p.longitude === state.waypoints[i].longitude,
    )
  ) {
    priorSegments = state.segments.slice(0, -1);
  }

  const nextSegments: ReadonlyArray<DrawingSegment> =
    nextWaypoints.length >= 2
      ? [...priorSegments, { mode: 'free', points: nextWaypoints }]
      : priorSegments;

  return { mode: state.mode, waypoints: nextWaypoints, segments: nextSegments };
}

/**
 * Free-draw drag stroke. Same as `appendFreeVertex` semantically but
 * intended for high-frequency mousemove events. Caller is expected to
 * throttle so we don't accumulate hundreds of near-identical points;
 * this function only filters out exact duplicates as a safety net.
 */
export function appendFreeDragSample(state: DrawingState, point: Coordinate): DrawingState {
  if (state.mode !== 'free') return state;
  const last = state.waypoints[state.waypoints.length - 1];
  if (
    last !== undefined &&
    last.latitude === point.latitude &&
    last.longitude === point.longitude
  ) {
    return state;
  }
  return appendFreeVertex(state, point);
}

/**
 * Undo the last user click in either mode. Only affects the current
 * mode's running stroke — segments accepted before the last mode
 * toggle stay put. Rationale: from the user's perspective "Ångra" is
 * "undo the last thing I clicked", and the last click is necessarily
 * in the current mode (mode-toggle clears `waypoints`).
 *
 * Snap mode: drops the last waypoint **and** the trailing snap segment
 * that was produced by it (segments count == waypoints count - 1 once
 * the first non-recording click has been made).
 *
 * Free mode: drops the last vertex; rebuilds the running free-segment
 * from the remaining waypoints. Drops the running segment entirely if
 * fewer than two vertices remain.
 *
 * No-op when the current mode's `waypoints` list is empty.
 */
export function undoLast(state: DrawingState): DrawingState {
  if (state.waypoints.length === 0) return state;

  const nextWaypoints = state.waypoints.slice(0, -1);

  if (state.mode === 'snap-to-road') {
    // Drop the trailing snap segment if one was produced by the last
    // waypoint. The pre-mode-toggle segments are immutable from undo's
    // perspective; check that the trailing segment is mode='snap-to-road'
    // before peeling it off.
    let nextSegments = state.segments;
    const trailing = state.segments[state.segments.length - 1];
    if (trailing !== undefined && trailing.mode === 'snap-to-road') {
      // First snap waypoint (the one without a segment) leaves segments
      // intact; subsequent waypoints each added one snap segment.
      const snapWaypointsBefore = state.waypoints.length;
      if (snapWaypointsBefore > 1) {
        nextSegments = state.segments.slice(0, -1);
      }
    }
    return { mode: state.mode, waypoints: nextWaypoints, segments: nextSegments };
  }

  // Free mode: rebuild the running free-segment from remaining
  // waypoints. Pre-mode-toggle segments are preserved.
  const trailing = state.segments[state.segments.length - 1];
  const priorSegments: ReadonlyArray<DrawingSegment> =
    trailing !== undefined && trailing.mode === 'free'
      ? state.segments.slice(0, -1)
      : state.segments;
  const nextSegments: ReadonlyArray<DrawingSegment> =
    nextWaypoints.length >= 2
      ? [...priorSegments, { mode: 'free', points: nextWaypoints }]
      : priorSegments;
  return { mode: state.mode, waypoints: nextWaypoints, segments: nextSegments };
}

/** Hard-clear the drawing — used by the "Avbryt"-button. */
export function cancelDrawing(state: DrawingState): DrawingState {
  return createInitialDrawingState(state.mode);
}

/**
 * Flatten the in-progress segments into the polylines that will be
 * submitted to the save endpoint (S-04-06). For now the saved hearth
 * geometry is `List<Polyline>`. Each segment becomes one polyline, which
 * is the natural shape for a multi-segment drawing (snap + free + snap).
 *
 * Returns an empty array when the drawing has no complete segments yet.
 */
export function buildPolylines(state: DrawingState): Polyline[] {
  return state.segments.filter((s) => s.points.length >= 2).map((s) => ({ points: [...s.points] }));
}
